import React, { useEffect, useState } from 'react';
import { VIEW_TYPE } from "../../../../../enums/ViewType";
import TableFunction from "../../../../../components/table/TableFunction";
import { DeleteIconsOptions, UpdateIconsOptions } from "../../../../../components/buttons/IconButtons";
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem";
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile";
import CustomUserChip from "../../../../../components/CustomUserChip";
import CustomChip from "../../../../../components/CustomChip";
import { colors } from "../../../../../enums/colors";
import HandleChange from "../../../../../Helpers/HandleChange";
import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import { POSITION_MODEL } from "../../../../../Helpers/PositionModel";
import {CUSTOM_FIELD_MODEL, getFieldTypeById} from "../Model/Model";

/**
 * @Email : kpozos@ibl.mx
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer
 *
 * Custom hook to manage the logic for the custom fields table.
 * Includes rendering, pagination, selection, edit, and delete functionality.
 *
 * @param {Array} fields - The list of custom fields.
 * @param {Function} setFields - Function to update the custom fields list.
 * @param {Object} fieldData - The current custom field data being edited or created.
 * @param {Function} setFieldData - Function to update the current custom field data.
 *
 * @returns {Object} An object exposing state variables and handler functions for the custom fields table.
 */
const CustomFieldsTableApp = ({ fields, setFields, fieldData, setFieldData }) => {
    // Local state for table and form management
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [editField, setEditField] = useState(CUSTOM_FIELD_MODEL);
    const [editModal, setEditModal] = useState(false);
    const [selectedList, setSelectedList] = useState([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const [tableId] = useState("tbl_cfta_custom_fields");
    const [searchId] = useState("txt_cfta_custom_fields");
    const [results, setResults] = useState([]);

    // Handle form field changes
    const { handleChange } = HandleChange({ model: fieldData, setModel: setFieldData });

    /**
     * Defines the table header columns.
     * @returns {Array} Array of JSX elements for table headers.
     */
    const getThead = () => {
        return [
            <CustomTh tableId={tableId}>{t('name')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('type')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('required')}</CustomTh>,
        ];
    };

    /**
     * Builds the table body rows based on the current results.
     * @returns {Object} Object containing the body JSX array and raw data.
     */
    const getTbody = () => {
        if (!results || results.length === 0) return { body: [], data: [] };

        let body = results.map((item) => {
            let fieldTypeData = getFieldTypeById(item.fieldType);
            return (
                <>
                    <CustomTd tableId={tableId}>{item.fieldName}</CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomUserChip
                            className='mt-0 chip-secondary-150-100'
                            name={fieldTypeData.name}
                            Icon={fieldTypeData.icon}
                            showAvatar={true}
                            key={fieldTypeData.name}
                            variant="compact"
                            iconPosition={POSITION_MODEL.LEFT}
                            classNameIcon={"icon-color-secondary-700"}
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomChip
                            variant="large"
                            background={colors.success100}
                            borderColor={colors.success150}
                            fontColor={colors.success700}
                            legend={item.isRequired ? t('YES') : t('NO')}
                        />
                    </CustomTd>
                </>
            );
        });

        return { body, data: results };
    };

    /**
     * Builds action buttons (edit, delete) for a given table row.
     * @param {Object} item - The data item for the row.
     * @param {number} index - The index of the row.
     * @returns {JSX.Element} The action buttons component.
     */
    const getActionsButtons = (item, index) => {
        let id = item.id;
        return (
            <DropDownMenuProfile
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                menuId={`menu-${id}`}
            >
                <DropDownMenuItem
                    id={`btn_cfta_update_${id}`}
                    index={index}
                    handleClose={handleClose}
                    name={t('update')}
                    click={() => {
                        setEditField({ ...item });
                        setEditModal(true);
                    }}
                    icon={<UpdateIconsOptions />}
                />
                <DropDownMenuItem
                    id={`btn_cfta_delete_${id}`}
                    index={index}
                    handleClose={handleClose}
                    name={t('delete')}
                    click={() => {
                        handleCheckboxChange({ target: { id, checked: true } });
                        setDeleteModal(true);
                    }}
                    fontColor="font-color-error-600"
                    iconColor={"icon-color-error-code-600"}
                    icon={<DeleteIconsOptions />}
                />
            </DropDownMenuProfile>
        );
    };

    // Table logic handled via TableFunction helper
    const {
        t,
        thead,
        tbody,
        handleCheckboxChange,
        handleDeleteAll,
        pagination,
        page,
        rowsPerPage,
        openMenu,
        handleMenuToggle,
        handleClose
    } = TableFunction({
        selectedList,
        setSelectedList,
        data: { content: results, totalElements: fields.length },
        labelRowsPerPage: 'custom_fields_per_page',
        hasContainer: true,
        fixedPagination: false,
        setIsAllChecked,
        view: VIEW_TYPE.LIST.name,
        getThead,
        getTbody,
    });

    /**
     * Updates displayed data whenever pagination or fields data changes.
     */
    useEffect(() => {
        getData(fields);
    }, [page, rowsPerPage, fields]);

    /**
     * Updates results state to display the current page of data.
     * @param {Array} data - The data to paginate.
     */
    const getData = (data) => {
        setResults(data.slice(page * rowsPerPage, (page + 1) * rowsPerPage));
    };

    /**
     * Adds a new custom field to the table.
     */
    const handleAddField = () => {
        let aux = [...fields, { ...fieldData, id: fields.length }];
        setFields(aux);
        getData(aux);
        setFieldData({ ...CUSTOM_FIELD_MODEL });
    };

    /**
     * Deletes custom fields by ID and updates the table data.
     * @param {Array} ids - The IDs of the fields to delete.
     */
    const deleteFn = (ids) => {
        let aux = fields.filter(e => !ids.includes(e.id));
        aux = aux.map((e, i) => (typeof e.id === 'string' ? e : { ...e, id: i }));
        setFields(aux);
        getData(aux);
        setSelectedList([]);
        setIsAllChecked(false);
    };

    // Return exposed states and handlers for the consuming component
    return {
        t,
        selectedList,
        setSelectedList,
        deleteModal,
        setDeleteModal,
        tableId,
        searchId,
        isAllChecked,
        setIsAllChecked,
        thead,
        tbody,
        editModal,
        setEditModal,
        editField,
        setEditField,
        getActionsButtons,
        pagination,
        deleteFn,
        handleCheckboxChange,
        handleDeleteAll,
        handleAddField,
        handleChange
    };
};

export default CustomFieldsTableApp;
